import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIf } from '@angular/common';
import { EmailFormComponent } from './forms/email-form/email-form.component';
import { PasswordFormComponent } from './forms/password-form/password-form.component';
import { CodeFormComponent } from './forms/code-form/code-form.component';
import { PersonalInformationFormComponent } from './forms/personal-information-form/personal-information-form.component';
import { BioFormComponent } from './forms/bio-form/bio-form.component';
import { ResumeFormComponent } from './forms/resume-form/resume-form.component';
import { ConsentFormComponent } from './forms/consent-form/consent-form.component';
import { RegistrationPageService, IConsentPayload } from './registration-page.service';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import {
  BIPPERZ_BREAKPOINTS,
  BreakpointsService,
  MediaQueryResultMap,
} from '../../../shared/services/breakpoint.service';
import { ShowPopupService } from '../../../shared/services/show-popup.service';
import { PATH } from '../../../../assets/constants';
import { ExternalHeaderComponent } from '../../../shared/external-header/external-header.component';
import { LayoutRegistrationPageComponent } from './layout-registration-page/layout-registration-page.component';
import { FooterComponent } from '../../../shared/footer/footer.component';
import { LoadMaskComponent } from '../../../shared/load-mask/load-mask.component';
import {
  IAuth,
  IEducation,
  IUser,
  IWorkExperience,
  MaterialType,
  UserRole
} from '../../../../assets/types/dtoTypes';

@Component({
  selector: 'app-registration-page',
  templateUrl: './registration-page.component.html',
  styleUrls: ['./registration-page.component.scss'],
  imports: [
    ExternalHeaderComponent,
    LayoutRegistrationPageComponent,
    FooterComponent,
    LoadMaskComponent,
    NgIf
  ],
  standalone: true
})
export class RegistrationPageComponent implements OnInit, OnDestroy {
  private readonly popupService = inject(ShowPopupService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly breakpointsService = inject(BreakpointsService);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly registrationPageService = inject(RegistrationPageService);
  private readonly authenticationService = inject(AuthenticationService);

  emailFormComponent = EmailFormComponent;
  passwordFormComponent = PasswordFormComponent;
  consentFormComponent = ConsentFormComponent;
  codeFormComponent = CodeFormComponent;
  personalInformationFormComponent = PersonalInformationFormComponent;
  bioFormComponent = BioFormComponent;
  resumeFormComponent = ResumeFormComponent;

  isLoading = false;

  cvUrl!: string;

  isEmailPage = true;
  isPasswordPage = false;
  isConsentPage = false;
  isCodePage = false;
  isPersonalInformationPage = false;
  isBioFormPage = false;
  isResumeFormPage = false;
  isUserForm = true;

  user: IUser = {} as IUser;
  consents: IConsentPayload[] = [];

  id!: string;
  isMobile = false;
  breakpoints$!: Observable<MediaQueryResultMap>;
  isAuthenticated = false;
  private isPhotoRecoveryFlow = false;
  private photoRecoveryNeedsCv = false;
  private isSocialSignupFlow = false;
  private socialSignupProvider: 'google' | 'apple' | 'social' = 'social';

  private subscriptions: Subscription[] = [];

  constructor() {
    this.breakpointsService.observe([BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down]).subscribe((result) => {
      this.isMobile = result.s || result.m_down;
    });

    this.subscriptions.push(this.registrationPageService.openNextForm.subscribe(form => this.changeForm(form)));
    this.subscriptions.push(this.registrationPageService.email.subscribe((emailAndUsername) => this.appendUserEmail(emailAndUsername)));
    this.subscriptions.push(this.registrationPageService.password.subscribe(password => this.appendUserPassword(password)));
    this.subscriptions.push(this.registrationPageService.consent.subscribe(consents => this.appendUserConsents(consents)));
    this.subscriptions.push(this.registrationPageService.code.subscribe(code => this.appendUserCode(code)));
    this.subscriptions.push(this.registrationPageService.personalInformation.subscribe(info => this.appendUserInfo(info)));
    this.subscriptions.push(this.registrationPageService.bio.subscribe((profileImage) => this.appendBio(profileImage)));
    this.subscriptions.push(this.registrationPageService.parsedResume.subscribe((data) => this.handleCvParsedData(data)));

    this.id = this.authenticationService.getIdFromAccessToken();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const error = params.error;
      const socialAccountErrorKey = this.resolveSocialAccountErrorTranslationKey(error);
      if (socialAccountErrorKey) {
        this.popupService.showPopup({
          status: 'failure',
          translationKey: socialAccountErrorKey,
          isBackToLogin: true
        });
        localStorage.removeItem('isGoogleRegistration');
        localStorage.removeItem('socialRegistrationProvider');
        this.router.navigate([], { queryParams: {}, replaceUrl: true });
        return;
      }

      if (error === 'email-already-registered' || error === 'user-already-exists') {
        this.popupService.showPopup({
          status: 'failure',
          translationKey: 'job_details.popup.application.emailAlreadyRegistered',
          isBackToLogin: true
        });
        localStorage.removeItem('isGoogleRegistration');
        localStorage.removeItem('socialRegistrationProvider');
        this.router.navigate([], { queryParams: {}, replaceUrl: true });
        return;
      }

      const token = params.token;
      const onboarding = params.onboarding;
      const isGoogleRegistration = localStorage.getItem('isGoogleRegistration') === 'true';
      const socialProvider = localStorage.getItem('socialRegistrationProvider');

      if (token) {
        this.authenticationService.setAccessToken(token);
        this.id = this.authenticationService.getIdFromAccessToken();

        if (isGoogleRegistration) {
          localStorage.removeItem('isGoogleRegistration');
          this.enterSocialConsentFlow((socialProvider as 'google' | 'apple' | 'social') || 'google');
        }
      }

      if (onboarding === 'cv' && this.id) {
        this.openCvOnboarding();
      } else if (onboarding === 'photo' && this.id) {
        this.openPhotoOnboarding();
      }
    });
  }

  appendUserEmail(emailAndUsername: { email: string, firstname: string, familyname: string }): void {
    this.user = {
      ...this.user,
      role: UserRole.USER,
      email: emailAndUsername.email,
      firstname: emailAndUsername.firstname,
      lastname: emailAndUsername.familyname,
    };
  }

  appendUserPassword(password: string): void {
    this.user = { ...this.user, password };
    this.changeForm('password_form_continue');
  }

  appendUserConsents(consents: IConsentPayload[]): void {
    this.consents = consents;

    if (this.isSocialSignupFlow) {
      this.submitSocialSignupConsents();
      return;
    }

    this.submit();
  }

  public submit(): void {
    this.isLoading = true;

    this.registrationPageService.registerUser({
      email: this.user.email,
      password: this.user.password,
      consents: this.consents
    }).subscribe({
      next: () => {
        this.isAuthenticated = true;
        this.isLoading = false;
        this.changeForm('consent_form_continue');
      },
      error: (error) => {
        this.isLoading = false;
        const message = this.extractErrorMessage(error);

        if (message === 'VERIFICATION_PENDING') {
          this.isAuthenticated = true;
          this.changeForm('consent_form_continue');
          return;
        }

        if (this.isEmailAlreadyRegisteredError(message)) {
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'job_details.popup.application.emailAlreadyRegistered',
            isBackToLogin: true
          });
          return;
        }

        this.popupService.showPopup({
          status: 'failure',
          translationKey: 'job_details.popup.application.generalError',
          isBackToLogin: false
        });
      }
    });
  }

  private submitSocialSignupConsents(): void {
    this.isLoading = true;
    const locale = localStorage.getItem('app_language') || 'de';
    const source = `${this.socialSignupProvider}_signup`;

    this.registrationPageService.submitSocialSignupConsents({
      locale,
      source,
      consents: this.consents
    }).subscribe({
      next: () => {
        this.isLoading = false;
        this.id = this.authenticationService.getIdFromAccessToken();
        this.isSocialSignupFlow = false;
        localStorage.removeItem('socialRegistrationProvider');
        this.showBioFormOnly();
      },
      error: (error) => {
        this.isLoading = false;
        const message = this.extractErrorMessage(error).toUpperCase();
        if (message.includes('REQUIRED_CONSENTS_MISSING')) {
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'job_details.popup.application.generalError',
            isBackToLogin: false
          });
          return;
        }
        this.popupService.showPopup({
          status: 'failure',
          translationKey: 'job_details.popup.application.generalError',
          isBackToLogin: false
        });
      }
    });
  }

  public appendUserCode(code: string): void {
    this.isLoading = true;
    this.verify(code);
  }

  public verify(code: string): void {
    this.registrationPageService.verifyUser(this.user.email, code, this.user.role)
      .subscribe((data: IAuth) => {
        this.authenticationService.setAccessToken(data.accessToken);
        this.id = this.authenticationService.getIdFromAccessToken();

        this.registrationPageService.updateUser(this.id, this.user).subscribe(() => {
          this.isEmailPage = false;
          this.isPasswordPage = false;
          this.isConsentPage = false;
          this.isCodePage = false;
          this.isBioFormPage = true;
          this.isLoading = false;
        });
      });
  }

  appendBio(profileImage: string): void {
    this.user.profileImageUrl = profileImage;
    this.user = { ...this.user };

    if (this.isPhotoRecoveryFlow) {
      this.isPhotoRecoveryFlow = false;
      if (this.photoRecoveryNeedsCv) {
        this.showResumeFormOnly();
      } else {
        this.router.navigate([`/main/${this.id}`]);
      }
      return;
    }

    this.isPersonalInformationPage = true;
    this.isBioFormPage = false;
  }

  appendUserInfo(info: { country: string, institutionCode: string, lookingFor: string, domain: string }): void {
    this.user = {
      ...this.user,
      location: info.country,
      lookingFor: info.lookingFor,
      domain: info.domain,
      institutionCode: info.institutionCode
    };

    this.registrationPageService.updateUser(this.id, this.user);
  }

  handleCvParsedData(data: {
    cvUrl: string;
    education: IEducation[];
    motivation: string;
    languages: string[];
    workExperience: IWorkExperience[];
  }): void {
    this.cvUrl = data.cvUrl;
    this.user = {
      ...this.user,
      motivation: data.motivation,
      languages: data.languages,
      education: data.education,
      workExperience: data.workExperience,
    };
  }

  appendParsedCVInfos(): void {
    this.registrationPageService.createNewMaterial({
      url: this.cvUrl,
      userId: this.id,
      type: MaterialType.CV
    }).subscribe(() => {
      this.registrationPageService.updateUser(this.id, this.user).subscribe({
        next: () => {
          this.router.navigate([`${PATH.PRICING}/${this.id}`]);
        },
        error: (error) => {
          console.error(error);
        }
      });
    });
  }

  changeForm(formName: string): void {
    if (formName === 'email_form') {
      this.isEmailPage = false;
      this.isPasswordPage = true;
      this.isConsentPage = false;
      this.isCodePage = false;
    }

    if (formName === 'password_form_continue') {
      this.isEmailPage = false;
      this.isPasswordPage = false;
      this.isConsentPage = true;
      this.isCodePage = false;
    }

    if (formName === 'password_form_back') {
      this.isEmailPage = true;
      this.isPasswordPage = false;
      this.isConsentPage = false;
      this.isCodePage = false;
    }

    if (formName === 'consent_form_back') {
      if (this.isSocialSignupFlow) {
        this.isEmailPage = true;
        this.isPasswordPage = false;
        this.isConsentPage = false;
        this.isCodePage = false;
        this.isPersonalInformationPage = false;
        this.isBioFormPage = false;
        this.isResumeFormPage = false;
        this.isSocialSignupFlow = false;
        localStorage.removeItem('socialRegistrationProvider');
        return;
      }
      this.isEmailPage = false;
      this.isPasswordPage = true;
      this.isConsentPage = false;
      this.isCodePage = false;
    }

    if (formName === 'social_signup_consent') {
      this.id = this.authenticationService.getIdFromAccessToken();
      const socialProvider = localStorage.getItem('socialRegistrationProvider');
      this.enterSocialConsentFlow((socialProvider as 'google' | 'apple' | 'social') || 'apple');
    }

    if (formName === 'consent_form_continue' && this.isAuthenticated) {
      this.isEmailPage = false;
      this.isPasswordPage = false;
      this.isConsentPage = false;
      this.isCodePage = true;
    }

    if (formName === 'personal_information_form') {
      this.isEmailPage = false;
      this.isPasswordPage = false;
      this.isConsentPage = false;
      this.isCodePage = false;
      this.isPersonalInformationPage = false;
      this.isBioFormPage = false;
      this.isResumeFormPage = true;
    }

    if (formName === 'bio_form_continue') {
      this.isEmailPage = false;
      this.isPasswordPage = false;
      this.isConsentPage = false;
      this.isCodePage = false;
      this.isPersonalInformationPage = true;
      this.isBioFormPage = false;
    }

    if (formName === 'bio_form_back') {
      this.isEmailPage = false;
      this.isPasswordPage = false;
      this.isConsentPage = false;
      this.isCodePage = false;
      this.isPersonalInformationPage = true;
      this.isBioFormPage = false;
    }

    if (formName === 'resume_form_continue') {
      this.isEmailPage = false;
      this.isPasswordPage = false;
      this.isConsentPage = false;
      this.isCodePage = false;
      this.isPersonalInformationPage = false;
      this.isBioFormPage = false;
      this.isResumeFormPage = false;
      this.appendParsedCVInfos();
    }

    if (formName === 'resume_form_back') {
      this.isEmailPage = false;
      this.isPasswordPage = false;
      this.isConsentPage = false;
      this.isCodePage = false;
      this.isPersonalInformationPage = true;
      this.isBioFormPage = false;
      this.isResumeFormPage = false;
    }
  }

  private skipRoleSelection(): void {
    this.isUserForm = false;
    this.isEmailPage = false;
    this.isPasswordPage = false;
    this.isConsentPage = false;
    this.isCodePage = false;
    this.isPersonalInformationPage = false;
    this.isResumeFormPage = false;
    this.isBioFormPage = true;
    this.cdr.detectChanges();
  }

  private enterSocialConsentFlow(provider: 'google' | 'apple' | 'social'): void {
    this.isSocialSignupFlow = true;
    this.socialSignupProvider = provider;
    this.isUserForm = false;
    this.isEmailPage = false;
    this.isPasswordPage = false;
    this.isConsentPage = true;
    this.isCodePage = false;
    this.isPersonalInformationPage = false;
    this.isResumeFormPage = false;
    this.isBioFormPage = false;
    this.cdr.detectChanges();
  }

  private openCvOnboarding(): void {
    this.registrationPageService.getUser(this.id).subscribe({
      next: (user) => {
        this.user = { ...this.user, ...user };
        this.showResumeFormOnly();
      },
      error: () => {
        this.showResumeFormOnly();
      }
    });
  }

  private openPhotoOnboarding(): void {
    this.registrationPageService.getUser(this.id).subscribe({
      next: (user) => {
        this.user = { ...this.user, ...user };
        const hasCv = (user.materials ?? []).some((material) => material.type === MaterialType.CV);
        this.isPhotoRecoveryFlow = true;
        this.photoRecoveryNeedsCv = !hasCv;
        this.showBioFormOnly();
      },
      error: () => {
        this.isPhotoRecoveryFlow = true;
        this.photoRecoveryNeedsCv = true;
        this.showBioFormOnly();
      }
    });
  }

  private showBioFormOnly(): void {
    this.isUserForm = false;
    this.isEmailPage = false;
    this.isPasswordPage = false;
    this.isConsentPage = false;
    this.isCodePage = false;
    this.isPersonalInformationPage = false;
    this.isResumeFormPage = false;
    this.isBioFormPage = true;
    this.cdr.detectChanges();
  }

  private showResumeFormOnly(): void {
    this.isUserForm = false;
    this.isEmailPage = false;
    this.isPasswordPage = false;
    this.isConsentPage = false;
    this.isCodePage = false;
    this.isPersonalInformationPage = false;
    this.isBioFormPage = false;
    this.isResumeFormPage = true;
    this.cdr.detectChanges();
  }

  private extractErrorMessage(error: any): string {
    return (
      error?.error?.message ??
      error?.error?.error ??
      error?.message ??
      ''
    );
  }

  private isEmailAlreadyRegisteredError(message: string): boolean {
    const normalizedMessage = (message ?? '').toUpperCase();
    return normalizedMessage.includes('EMAIL_TAKEN')
      || normalizedMessage.includes('EMAIL-ALREADY-REGISTERED')
      || normalizedMessage.includes('USER-ALREADY-EXISTS')
      || normalizedMessage.includes('ACCOUNT_EXISTS_WITH_GOOGLE')
      || normalizedMessage.includes('ACCOUNT_EXISTS_WITH_APPLE')
      || normalizedMessage.includes('ACCOUNT_EXISTS_WITH_PASSWORD')
      || normalizedMessage.includes('DUPLICATE ENTRY')
      || normalizedMessage.includes('ALREADY REGISTERED');
  }

  private resolveSocialAccountErrorTranslationKey(error: string): string | null {
    if (error === 'account-exists-with-google') {
      return 'job_details.popup.application.accountExistsWithGoogle';
    }
    if (error === 'account-exists-with-apple') {
      return 'job_details.popup.application.accountExistsWithApple';
    }
    if (error === 'account-exists-with-password') {
      return 'job_details.popup.application.accountExistsWithPassword';
    }
    return null;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
